import React, { useEffect, useState } from 'react';
import {
  Box,
  Icon,
  InlineMessage,
  InputField,
  Switch,
  Typography,
} from '@saviynt/design-system';

import { CopyInputField, LoadingState } from '../../../shared/components';
import { useSelector } from '../../../shared/utils';
import { useInvertedLocalize } from '../../../shared/utils/localization';
import { importSamlConfig } from '../data-sources';
import { type ProviderEditProps, type SamlProvider } from '../types';
import { type SamlProviderMetadataFileProperties } from '../types/saml';
import { samlMetadataParser } from '../utils';
import { toNameIdUrn } from '../utils/nameIdFormats';

import { MetadataFilePicker } from './MetadataFilePicker';

import './SAMLEditView.css';

const NBSP = '\u00A0';

enum LabelKeys {
  redirectUrl = 'AUTH_SamlEditView_redirectUrl',
  spEntityId = 'AUTH_SamlEditView_spEntityId',
  displayName = 'AUTH_SamlEditView_displayName',
  alias = 'AUTH_SamlEditView_alias',
  logoutServiceUrl = 'AUTH_SamlEditView_logoutServiceUrl',
  nameIDPolicyFormat = 'AUTH_SamlEditView_nameIDPolicyFormat',
}

const defaultMetadataProps: SamlProviderMetadataFileProperties = {
  protocol: 'saml',
  logoutServiceUrl: '',
  wantsAuthRequestsSigned: false,
  nameIDPolicyFormat: '',
};

function validateMetadata(
  provider: SamlProvider,
  needsUrl: boolean,
  hasParseError: boolean
) {
  if (hasParseError) {
    return false;
  }

  if (needsUrl) {
    return !!provider.entityDescriptorUrl;
  }

  return true;
}

const { protocol, host: domain } = window.location;

function getRedirectUrl(realmName: string, alias: string) {
  return alias
    ? `${protocol}//${domain}/auth/realms/${realmName}/broker/${alias}/endpoint`
    : NBSP;
}

/** View to edit a SAML configuration */
export default function SAMLEditView({
  props: {
    provider,
    validateAlias,
    validateName,
    setProvider,
    setCanSave,
    isNew,
    isLoading,
    error,
  },
  isFullwidth,
}: {
  props: ProviderEditProps<SamlProvider>;
  isFullwidth: boolean;
}): JSX.Element {
  const l = useInvertedLocalize();
  const realmName = useSelector((s) => s.user.domainInfo.MTA_REALM_NAME);

  const [parseError, setParseError] = useState('');
  const [hasUpdatedMetadata, setHasUpdatedMetadata] = useState(false);
  const [hasLoadedMetadata, setHasLoadedMetadata] = useState(false);
  const [isNameIdFromFile, setIsNameIdFromFile] = useState(false);
  const [isUrlLoading, setIsUrlLoading] = useState(false);
  const [isUrlSuccess, setIsUrlSuccess] = useState(false);

  const nameInfo = validateName(provider);
  const aliasInfo = validateAlias(provider);
  const isMetadataValid = validateMetadata(
    provider,
    isNew || hasUpdatedMetadata,
    !!parseError
  );

  const setDisplayName = (value: string) => {
    setProvider((prev) => ({
      ...prev,
      name: value,
    }));
  };

  const setAlias = (value: string) => {
    setProvider((prev) => ({
      ...prev,
      alias: value,
    }));
  };

  const setSpEntityId = (value: string) => {
    setProvider((prev) => ({
      ...prev,
      spEntityId: value,
    }));
  };

  /** Apply parsed metadata properties to provider state */
  const applyMetadata = (
    parsed: SamlProviderMetadataFileProperties,
    entityDescriptorUrl?: string
  ) => {
    setParseError('');
    setHasLoadedMetadata(true);
    setIsNameIdFromFile(!!parsed.nameIDPolicyFormat);
    setProvider((prev) => ({
      ...prev,
      ...parsed,
      entityDescriptorUrl,
    }));
  };

  const resetMetadata = () => {
    setHasLoadedMetadata(false);
    setIsNameIdFromFile(false);
    setProvider((prev) => ({
      ...prev,
      ...defaultMetadataProps,
      entityDescriptorUrl: undefined,
    }));
  };

  /** Handle file upload: parse XML client-side */
  const onFileChange = ({ url, data }: { url?: string; data?: string }) => {
    setHasUpdatedMetadata(true);

    if (!data) {
      resetMetadata();

      return;
    }

    const parsed = samlMetadataParser(data, l);

    if ('isError' in parsed) {
      setParseError(parsed.message);
      resetMetadata();

      return;
    }

    applyMetadata(parsed, url);
  };

  /** Handle URL load: call backend proxy */
  const onLoadUrl = async (metadataUrl: string) => {
    setHasUpdatedMetadata(true);
    setIsUrlLoading(true);
    setIsUrlSuccess(false);
    setParseError('');

    try {
      const parsed = await importSamlConfig(metadataUrl);

      applyMetadata(parsed, metadataUrl);
      setIsUrlSuccess(true);
    } catch {
      setParseError(l('Failed to load metadata file'));
      resetMetadata();
    } finally {
      setIsUrlLoading(false);
    }
  };

  const onNameIdFormatChange = (value: string) => {
    const urn = toNameIdUrn(value);

    setProvider((prev) => ({
      ...prev,
      nameIdFormat: urn,
      nameIDPolicyFormat: urn,
      // Clear principal fields when switching away from transient
      ...(value !== 'transient'
        ? { principalType: '', principalAttribute: '' }
        : {}),
    }));
  };

  const onPrincipalTypeChange = (value: string) => {
    setProvider((prev) => ({ ...prev, principalType: value }));
  };

  const onPrincipalAttributeChange = (value: string) => {
    setProvider((prev) => ({ ...prev, principalAttribute: value }));
  };

  const shouldShowMetadata =
    hasLoadedMetadata ||
    !!(provider.logoutServiceUrl || provider.nameIDPolicyFormat);

  // Principal type is required when user manually selects Transient
  const needsPrincipalType =
    !isNameIdFromFile &&
    provider.nameIdFormat ===
      'urn:oasis:names:tc:SAML:2.0:nameid-format:transient';
  const isPrincipalTypeValid = !needsPrincipalType || !!provider.principalType;

  useEffect(() => {
    setCanSave(
      nameInfo.isValid &&
        aliasInfo.isValid &&
        isMetadataValid &&
        isPrincipalTypeValid
    );

    // setCanSave doesn't matter for this update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nameInfo.isValid,
    aliasInfo.isValid,
    isMetadataValid,
    isPrincipalTypeValid,
  ]);

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <Box className='Auth_SAMLEditView'>
      {error && (
        <Box className='Auth_SAMLEditView_row'>
          <InlineMessage
            text={error}
            kind='background'
            colorTheme='critical'
            size='medium'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        </Box>
      )}
      <Box className='Auth_SAMLEditView_row'>
        <InputField
          name={LabelKeys.displayName}
          isRequired
          placeholder=''
          value={provider.name}
          setValue={setDisplayName}
          label={
            <Typography kind='label' htmlFor={LabelKeys.displayName}>
              {l('Display Name')}
            </Typography>
          }
          isCritical={!nameInfo.isValid && !!provider.name}
          CriticalHelperText={
            <InlineMessage
              text={nameInfo.message}
              colorTheme='critical'
              size='small'
              leftIcon={<Icon kind='AlertCriticalFilled' />}
            />
          }
        />
        <InputField
          name={LabelKeys.alias}
          isRequired
          isReadOnly={!isNew}
          readOnlyKind='boxed'
          placeholder=''
          value={provider.alias}
          setValue={setAlias}
          label={
            <Typography kind='label' htmlFor={LabelKeys.alias}>
              {l('Alias')}
            </Typography>
          }
          isCritical={!aliasInfo.isValid && !!provider.alias}
          CriticalHelperText={
            <InlineMessage
              text={aliasInfo.message}
              colorTheme='critical'
              size='small'
              leftIcon={<Icon kind='AlertCriticalFilled' />}
            />
          }
        />
      </Box>
      <Box className='Auth_SAMLEditView_row'>
        <CopyInputField
          id={LabelKeys.redirectUrl}
          label={l('Redirect URL')}
          isReadOnly
          value={getRedirectUrl(realmName, provider.alias)}
        />
        <CopyInputField
          id={LabelKeys.spEntityId}
          label={l('SP Entity ID')}
          value={provider.spEntityId}
          setValue={setSpEntityId}
        />
      </Box>
      <Typography kind='h2' className='Auth_SAMLEditView_h2'>
        {l('Security & Attributes')}
      </Typography>
      <Box className='Auth_SAMLEditView_row'>
        <Switch
          className='Auth_SAMLEditView_switch'
          label={l('Want Assertions Signed')}
          labelPosition='right'
          isChecked={provider.wantsAssertionsSigned}
          onChange={() => {
            setProvider((prev) => ({
              ...prev,
              wantsAssertionsSigned: !prev.wantsAssertionsSigned,
            }));
          }}
        />
        <Switch
          className='Auth_SAMLEditView_switch'
          label={l('Want Assertions Encrypted')}
          labelPosition='right'
          isChecked={provider.wantsAssertionsEncrypted}
          onChange={() => {
            setProvider((prev) => ({
              ...prev,
              wantsAssertionsEncrypted: !prev.wantsAssertionsEncrypted,
            }));
          }}
        />
      </Box>
      <Box className='Auth_SAMLEditView_row'>
        <Switch
          className='Auth_SAMLEditView_switch'
          label={l('Force Authentication')}
          labelPosition='right'
          isChecked={provider.shouldForceAuthentication}
          onChange={() => {
            setProvider((prev) => ({
              ...prev,
              shouldForceAuthentication: !prev.shouldForceAuthentication,
            }));
          }}
        />
        <Switch
          className='Auth_SAMLEditView_switch'
          label={l('Validate Signatures')}
          labelPosition='right'
          isChecked={provider.shouldValidateSignatures}
          onChange={() => {
            setProvider((prev) => ({
              ...prev,
              shouldValidateSignatures: !prev.shouldValidateSignatures,
            }));
          }}
        />
      </Box>
      <Box className='Auth_SAMLEditView_half'>
        <Switch
          className='Auth_SAMLEditView_switch'
          label={l('Sign Service Provider Metadata')}
          labelPosition='right'
          isChecked={provider.shouldSignSPMetadata}
          onChange={() => {
            setProvider((prev) => ({
              ...prev,
              shouldSignSPMetadata: !prev.shouldSignSPMetadata,
            }));
          }}
        />
      </Box>
      <Box
        className={
          // When component is rendered full width, we only want to take up 2/3 of the screen
          isFullwidth ? 'Auth_SAMLEditView_twoThirds' : ''
        }>
        <MetadataFilePicker
          label={l('How would you like to configure the entity descriptor?')}
          onFileChange={onFileChange}
          onLoadUrl={(url) => {
            onLoadUrl(url).catch(() => {});
          }}
          isUrlLoading={isUrlLoading}
          isUrlSuccess={isUrlSuccess}
          hasValue={shouldShowMetadata}
          error={parseError}
          setError={(err) => setParseError(err)}
          parsedNameIdFormat={provider.nameIDPolicyFormat}
          isNameIdFromFile={isNameIdFromFile}
          onNameIdFormatChange={onNameIdFormatChange}
          onPrincipalTypeChange={onPrincipalTypeChange}
          onPrincipalAttributeChange={onPrincipalAttributeChange}
          principalType={provider.principalType ?? ''}
          principalAttribute={provider.principalAttribute ?? ''}>
          {shouldShowMetadata && (
            <Box className='Auth_SAMLEditView'>
              <Typography kind='h2' className='Auth_SAMLEditView_h2'>
                {l('Logout & Binding')}
              </Typography>
              <Box className='Auth_SAMLEditView_half'>
                <InputField
                  name={LabelKeys.logoutServiceUrl}
                  placeholder=''
                  value={provider.logoutServiceUrl || NBSP}
                  isReadOnly
                  readOnlyKind='boxed'
                  label={
                    <Typography
                      kind='label'
                      htmlFor={LabelKeys.logoutServiceUrl}>
                      {l('Single Logout Service URL')}
                    </Typography>
                  }
                />
              </Box>
              <Box className='Auth_SAMLEditView_half'>
                <Switch
                  className='Auth_SAMLEditView_switch'
                  label={l('Want Authn Requests Signed')}
                  labelPosition='right'
                  isChecked={provider.wantsAuthRequestsSigned}
                  onChange={() => {}}
                  isDisabled
                />
              </Box>
              <Typography kind='h2' className='Auth_SAMLEditView_h2'>
                {l('NameID & Principal Settings')}
              </Typography>
              <Box className='Auth_SAMLEditView_half'>
                <InputField
                  name={LabelKeys.nameIDPolicyFormat}
                  placeholder=''
                  value={provider.nameIDPolicyFormat || NBSP}
                  isReadOnly
                  readOnlyKind='boxed'
                  label={
                    <Typography
                      kind='label'
                      htmlFor={LabelKeys.nameIDPolicyFormat}>
                      {l('NameID Policy Format')}
                    </Typography>
                  }
                />
              </Box>
            </Box>
          )}
        </MetadataFilePicker>
      </Box>
    </Box>
  );
}
